import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { FaTimes, FaCheck, FaInfoCircle, FaShieldAlt, FaChartBar, FaBullhorn, FaCog } from 'react-icons/fa';

const CookiePreferences = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const [preferences, setPreferences] = useState({
    essential: true, // Sempre attivo, non modificabile
    analytics: false,
    marketing: false,
    functionality: false
  });

  useEffect(() => {
    if (isOpen) {
      // Carica le preferenze esistenti se disponibili
      const existingConsent = localStorage.getItem('cosmo-cookie-consent');
      if (existingConsent) {
        try {
          const parsed = JSON.parse(existingConsent);
          setPreferences({
            essential: true,
            analytics: parsed.analytics || false,
            marketing: parsed.marketing || false,
            functionality: parsed.preferences || false
          });
        } catch (e) {
          console.error('Error parsing existing consent:', e);
        }
      }
      
      // Blocca lo scroll del body
      document.body.style.overflow = 'hidden';
    } else {
      // Ripristina lo scroll
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleToggle = (category) => {
    if (category === 'essential') return; // Non può essere disattivato
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSavePreferences = () => {
    const consentData = {
      essential: true,
      analytics: preferences.analytics,
      marketing: preferences.marketing,
      preferences: preferences.functionality,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    
    localStorage.setItem('cosmo-cookie-consent', JSON.stringify(consentData));
    
    // Aggiorna il consenso per Google Analytics/Tag Manager
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': preferences.analytics ? 'granted' : 'denied',
        'ad_storage': preferences.marketing ? 'granted' : 'denied',
        'functionality_storage': preferences.functionality ? 'granted' : 'denied',
        'personalization_storage': preferences.functionality ? 'granted' : 'denied'
      });
    }

    onClose();
  };

  const handleAcceptAll = () => {
    setPreferences({
      essential: true,
      analytics: true,
      marketing: true,
      functionality: true
    });
  };

  const handleRejectAll = () => {
    setPreferences({
      essential: true,
      analytics: false,
      marketing: false,
      functionality: false
    });
  };

  if (!isOpen) return null;

  const texts = {
    en: {
      title: "Cookie Preferences",
      description: "Manage your cookie preferences. You can enable or disable different types of cookies below.",
      savePreferences: "Save Preferences",
      acceptAll: "Accept All",
      rejectAll: "Reject All (Keep Essential)",
      categories: {
        essential: {
          title: "Essential Cookies",
          description: "These cookies are necessary for the website to function and cannot be switched off. They are usually only set in response to actions made by you which amount to a request for services.",
          examples: "Session management, security, basic functionality"
        },
        analytics: {
          title: "Analytics Cookies", 
          description: "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.",
          examples: "Google Analytics, usage statistics, performance monitoring"
        },
        marketing: {
          title: "Marketing Cookies",
          description: "These cookies are used to track visitors across websites to display relevant advertisements and marketing content.",
          examples: "Advertising networks, social media integration, remarketing"
        },
        functionality: {
          title: "Functionality Cookies",
          description: "These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings.",
          examples: "Language preferences, user interface customization, enhanced features"
        }
      },
      alwaysActive: "Always Active",
      examples: "Examples:"
    },
    it: {
      title: "Preferenze Cookie",
      description: "Gestisci le tue preferenze sui cookie. Puoi abilitare o disabilitare diversi tipi di cookie qui sotto.",
      savePreferences: "Salva Preferenze",
      acceptAll: "Accetta Tutti",
      rejectAll: "Rifiuta Tutti (Mantieni Essenziali)",
      categories: {
        essential: {
          title: "Cookie Essenziali",
          description: "Questi cookie sono necessari per il funzionamento del sito web e non possono essere disattivati. Vengono solitamente impostati solo in risposta ad azioni da te effettuate.",
          examples: "Gestione sessione, sicurezza, funzionalità di base"
        },
        analytics: {
          title: "Cookie Analitici",
          description: "Questi cookie ci aiutano a capire come i visitatori interagiscono con il nostro sito web raccogliendo e riportando informazioni in modo anonimo.",
          examples: "Google Analytics, statistiche di utilizzo, monitoraggio prestazioni"
        },
        marketing: {
          title: "Cookie Marketing",
          description: "Questi cookie vengono utilizzati per tracciare i visitatori attraverso i siti web per mostrare annunci pubblicitari e contenuti marketing pertinenti.",
          examples: "Reti pubblicitarie, integrazione social media, remarketing"
        },
        functionality: {
          title: "Cookie Funzionalità",
          description: "Questi cookie abilitano funzionalità avanzate e personalizzazione, come ricordare le tue preferenze e impostazioni.",
          examples: "Preferenze lingua, personalizzazione interfaccia, funzionalità avanzate"
        }
      },
      alwaysActive: "Sempre Attivo",
      examples: "Esempi:"
    }
  };

  const t = texts[language] || texts.en;

  const categoryIcons = {
    essential: FaShieldAlt,
    analytics: FaChartBar,
    marketing: FaBullhorn,
    functionality: FaCog
  };

  const categoryColors = {
    essential: "text-green-600 bg-green-100",
    analytics: "text-blue-600 bg-blue-100", 
    marketing: "text-purple-600 bg-purple-100",
    functionality: "text-indigo-600 bg-indigo-100"
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t.title}</h2>
              <p className="text-gray-600 mt-1">{t.description}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
            >
              <FaTimes size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            <div className="space-y-6">
              {Object.entries(t.categories).map(([key, category]) => {
                const Icon = categoryIcons[key];
                const isEnabled = preferences[key];
                const isEssential = key === 'essential';
                
                return (
                  <div key={key} className="border border-gray-200 rounded-lg p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${categoryColors[key]}`}>
                          <Icon className="text-lg" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {category.title}
                          </h3>
                          <p className="text-gray-600 text-sm leading-relaxed mb-3">
                            {category.description}
                          </p>
                          <div className="flex items-start gap-1 text-xs text-gray-500">
                            <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="font-medium">{t.examples}</span> {category.examples}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Toggle Switch */}
                      <div className="flex flex-col items-center gap-2 ml-4">
                        {isEssential ? (
                          <div className="flex items-center gap-2 px-3 py-2 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            <FaCheck size={10} />
                            {t.alwaysActive}
                          </div>
                        ) : (
                          <button
                            onClick={() => handleToggle(key)}
                            className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                              isEnabled ? 'bg-indigo-600' : 'bg-gray-300'
                            }`}
                          >
                            <div
                              className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200 ${
                                isEnabled ? 'translate-x-6' : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                        )}
                        <span className={`text-xs font-medium ${
                          isEnabled ? 'text-indigo-600' : 'text-gray-400'
                        }`}>
                          {isEnabled ? 'ON' : 'OFF'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleRejectAll}
              className="flex-1 px-4 py-3 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors"
            >
              {t.rejectAll}
            </button>
            <button
              onClick={handleAcceptAll}
              className="flex-1 px-4 py-3 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg font-medium transition-colors"
            >
              {t.acceptAll}
            </button>
            <button
              onClick={handleSavePreferences}
              className="flex-1 px-4 py-3 text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg font-medium transition-colors"
            >
              {t.savePreferences}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookiePreferences;